import { useState } from "react";
import "./CustomerActivity.css";

const currency = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });
const count = new Intl.NumberFormat("en-US");
const money = (value) => currency.format(Number(value || 0));
const date = (value) => value ? new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" }).format(new Date(`${value}T00:00:00Z`)) : "—";
const days = (value) => `${count.format(value)} ${value === 1 ? "day" : "days"}`;
export const INACTIVE_DAY_OPTIONS = [7, 14, 30, 60];

const GROUPS = {
  lost: {
    title: "Lost", requiresComparison: true,
    caption: (group) => `${money(group.previous_sales)} in the comparison period`,
    empty: "Every customer from the comparison period ordered again.",
    columns: [
      { label: "Last order", render: (row) => date(row.last_order_date) },
      { label: "Days since", render: (row) => days(row.days_since_last_order) },
      { label: "Orders before", render: (row) => count.format(row.previous_orders) },
      { label: "Comparison sales", render: (row) => money(row.previous_sales) },
    ],
  },
  inactive: {
    title: "Not ordering", requiresComparison: false,
    caption: (group) => `${money(group.lookback_sales)} over the last ${group.lookback_days} days`,
    empty: "Everyone who ordered recently has ordered within this window.",
    columns: [
      { label: "Last order", render: (row) => date(row.last_order_date) },
      { label: "Days since", render: (row) => days(row.days_since_last_order) },
      { label: "Orders (90 days)", render: (row) => count.format(row.lookback_orders) },
      { label: "Sales (90 days)", render: (row) => money(row.lookback_sales) },
    ],
  },
  new: {
    title: "New", requiresComparison: false,
    caption: (group) => `${money(group.sales)} from first-time customers`,
    empty: "No first-time customers in this period.",
    columns: [
      { label: "First order", render: (row) => date(row.first_order_date) },
      { label: "Orders", render: (row) => count.format(row.orders) },
      { label: "Sales", render: (row) => money(row.sales) },
    ],
  },
  reactivated: {
    title: "Back again", requiresComparison: true,
    caption: (group) => `${money(group.sales)} from customers who skipped the comparison period`,
    empty: "No returning customers in this period.",
    columns: [
      { label: "Customer since", render: (row) => date(row.first_order_date) },
      { label: "Orders", render: (row) => count.format(row.orders) },
      { label: "Sales", render: (row) => money(row.sales) },
    ],
  },
};
const ORDER = ["lost", "inactive", "new", "reactivated"];

export default function CustomerActivity({ customers, hasComparison, inactiveDays, onInactiveDaysChange, onSelectCustomer, loading = false }) {
  const [selected, setSelected] = useState(null);
  if (!customers) return null;
  const available = (key) => Boolean(customers[key]) && (!GROUPS[key].requiresComparison || hasComparison);
  const fallback = available("lost") && customers.lost.total > 0 ? "lost" : "inactive";
  const active = selected && available(selected) ? selected : fallback;
  const group = customers[active];
  const config = GROUPS[active];
  const counts = customers.counts || {};
  const retainedShare = hasComparison && counts.previous ? Math.round((counts.retained / counts.previous) * 100) : null;
  const tileDetail = (key) => {
    if (key === "inactive") return `No order in ${inactiveDays}+ days`;
    if (!available(key)) return "Choose a comparison";
    return key === "lost" ? "Ordered before, not this period" : key === "new" ? "First order ever" : "Returned after a gap";
  };

  return <section className="customer-activity" aria-label="Customer activity">
    <div className="sales-analytics-section-head">
      <div><span className="sales-analytics-eyebrow">Who’s coming and going</span><h2>Customer activity</h2></div>
      {retainedShare != null && <span className="sales-analytics-section-aside">Kept {count.format(counts.retained)} of {count.format(counts.previous)} customers from the comparison period ({retainedShare}%)</span>}
    </div>
    <div className="customer-activity-tiles" role="tablist" aria-label="Customer groups">
      {ORDER.map((key) => {
        const enabled = available(key);
        const total = enabled ? customers[key].total : null;
        return <button type="button" role="tab" key={key} id={`customer-activity-tab-${key}`} aria-selected={active === key} aria-controls="customer-activity-panel"
          disabled={!enabled || loading} className={`customer-activity-tile customer-activity-tile--${key}${active === key ? " is-selected" : ""}`} onClick={() => setSelected(key)}>
          <span className="customer-activity-tile-title">{GROUPS[key].title}</span>
          <strong>{total == null ? "—" : count.format(total)}</strong>
          <span className="customer-activity-tile-detail">{tileDetail(key)}</span>
        </button>;
      })}
    </div>
    <div className="customer-activity-panel" id="customer-activity-panel" role="tabpanel" aria-labelledby={`customer-activity-tab-${active}`}>
      <div className="customer-activity-panel-head">
        <p>{config.caption(group)}{group.total > group.rows.length && ` · showing the top ${group.rows.length} of ${count.format(group.total)}`}</p>
        {active === "inactive" && <label className="customer-activity-days">
          <span>No order in</span>
          <select value={inactiveDays} onChange={(event) => onInactiveDaysChange?.(Number(event.target.value))} disabled={loading}>
            {INACTIVE_DAY_OPTIONS.map((option) => <option key={option} value={option}>{option}+ days</option>)}
          </select>
        </label>}
      </div>
      {group.rows.length ? <div className="customer-activity-table-wrap"><table aria-label={`${config.title} customers`}>
        <thead><tr><th scope="col">Customer</th>{config.columns.map((column) => <th scope="col" key={column.label}>{column.label}</th>)}</tr></thead>
        <tbody>{group.rows.map((row) => <tr key={row.key}>
          <th scope="row"><button type="button" className="customer-activity-name" title={`Focus the page on ${row.name}`} onClick={() => onSelectCustomer?.(row)}>{row.name || "Unassigned"}</button></th>
          {config.columns.map((column) => <td key={column.label}>{column.render(row)}</td>)}
        </tr>)}</tbody>
      </table></div> : <p className="customer-activity-empty">{config.empty}</p>}
    </div>
  </section>;
}
